import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as Constants from 'src/app/models/Constants';
import { LogService } from 'src/app/services/log.service';
import { OtcService } from 'src/app/services/otc.service';
import { RootScopeService } from 'src/app/services/root-scope.service';

@Component({
  selector: 'app-one-time-code',
  templateUrl: './one-time-code.component.html',
  styleUrls: ['./one-time-code.component.scss']
})

export class OneTimeCodeComponent implements OnInit, OnDestroy {

  beforeStep1Active = Constants.AUTHENTICATION_OTC_STEPS.BEFORE_STEP1_ACTIVATE;
  onStep1Active = Constants.AUTHENTICATION_OTC_STEPS.ON_STEP1_ACTIVATE;
  onStep2Active = Constants.AUTHENTICATION_OTC_STEPS.ON_STEP2_ACTIVATE;
  onStep3Active = Constants.AUTHENTICATION_OTC_STEPS.ON_STEP3_ACTIVATE;
  generateOtcApiFailErrMsg = Constants.OTC_MESSAGES.GENERATE_OTC_API_FAIL_ERR_MSG;
  notEligibleErrMsg = Constants.OTC_MESSAGES.NOT_ELIGIBLE_ERR_MSG;
  deniedErrMsg = Constants.OTC_MESSAGES.DENIED_ERR_MSG;
  noConfigForDeliveryMethods = Constants.OTC_MESSAGES.NO_CONFIG_FOR_DELIVER_METHODS;
  accountLockedMsg = Constants.OTC_MESSAGES.ACCOUNT_LOCKED;
  invalidOTCMsg = Constants.OTC_MESSAGES.INVALID_OTC;
  validateOtcApiFailureMsg = Constants.OTC_MESSAGES.VALIDATE_OTC_API_FAIL_ERR_MSG;
  otcExpiredMsg = Constants.OTC_MESSAGES.OTC_EXPIRED;
  email = Constants.OTC_MODES.EMAIL;
  text = Constants.OTC_MODES.TEXT;
  customMobileOption = {};
  verificationTooltipIcon = { iconName: 'circle-info', iconType: 'light', iconSize: 's' };
  noDataOnFile = Constants.OTC_MESSAGES.NO_DATA_ON_FILE;
  noMobileNumberAvailableFtuOtcMsg = Constants.OTC_MESSAGES.NO_MOBILE_NUMBER_AVAILABLE_FTU_OTC_MSG;
  verifyCodeClicked: boolean = false;
  languageForm = new FormGroup({
    language: new FormControl('', Validators.required)
  });
  languageOptions: Array<{ localeCode: string; localeDesc: string }> = [];
  languageDropdownOptions: Array<{ clientId: string; clientName: string }> = [];
  otcStep1InfoMsg = Constants.OTC_MESSAGES.OTC_STEP1_INFO_MSG;

  fileName: string = "one-time-code_component";
  private otcSubscription?: Subscription;


  constructor(public rootScope: RootScopeService,
    public otcService: OtcService, public logger: LogService
  ) { }

  /**
   * CCR1-12401 & CCR1-12986 - The OTC flow has been updated to reduce the number of steps in the UI.
   * @author - Sultan Khan
   */

  ngOnInit(): void {
    this.rootScope.activeCustTab.otcData.validateOTCApiProcessing = false;
    this.initializeStep1LocaleOptions();
    this.syncSelectedLocaleFromDropdown();
    this.initAndSyncOTC();
  }

  private getSupportedLocalesFromPreAuth(): string[] {
    const fromPreAuth: any = this.rootScope?.accountSAuthConfig?.locales;
    let supportedLocales: string[] = [];

    if (Array.isArray(fromPreAuth)) {
      supportedLocales = fromPreAuth;
    }

    const localeValues = supportedLocales
      .map((locale: string) => (locale || '').trim())
      .filter((locale: string) => !!locale);

    return Array.from(new Set(localeValues));
  }

  private resolveLocaleDisplayName(localeCode: string): string {
    const code = (localeCode || '').trim();
    const preferredLocaleList = this.rootScope?.localeOTC || [];

    const localeMatch = preferredLocaleList.find((entry: any) => {
      const entryLocale = (entry?.fieldValue || '').trim();
      return entryLocale === code;
    });

    return localeMatch?.fieldValueDescription || code;
  }

  private initializeStep1LocaleOptions(): void {
    const otcData = this.rootScope.activeCustTab?.otcData;
    if (!otcData) {
      return;
    }

    let supportedLocales = this.getSupportedLocalesFromPreAuth();
    if (supportedLocales.length === 0) {
      supportedLocales = ['en_US'];
    }

    this.languageOptions = supportedLocales.map((localeCode: string) => ({
      localeCode,
      localeDesc: this.resolveLocaleDisplayName(localeCode)
    }));

    // Shared smart-search component expects clientId/clientName keys.
    this.languageDropdownOptions = this.languageOptions.map((option) => ({
      clientId: option.localeCode,
      clientName: option.localeDesc
    }));

    this.resetLanguageSelectionToDefault();
  }

  private syncSelectedLocaleFromDropdown(): void {
    this.languageForm.get('language')?.valueChanges?.subscribe((selectedLanguageName: string | null) => {
      const hasTypedValue = !!(selectedLanguageName || '').trim();
      if (!hasTypedValue) {
        this.rootScope.activeCustTab.otcData.preferredLocale = '';
        this.updateLanguageControlErrors(false, false);
        return;
      }

      const selectedOption = this.languageOptions.find((option) => option.localeDesc === selectedLanguageName);
      this.rootScope.activeCustTab.otcData.preferredLocale = selectedOption?.localeCode || '';
      this.updateLanguageControlErrors(hasTypedValue, !!selectedOption?.localeCode);
    });
  }

  private resetLanguageSelectionToDefault(): void {
    const otcData = this.rootScope.activeCustTab?.otcData;
    const languageControl = this.languageForm.get('language');
    if (!otcData || !languageControl) {
      return;
    }

    const localeCodes = this.languageOptions.map((option) => option.localeCode);
    const storedPreferredLocale = (otcData.preferredLocale || '').trim();
    const defaultLocale = localeCodes.includes(storedPreferredLocale)
      ? storedPreferredLocale
      : (localeCodes.includes('en_US') ? 'en_US' : (localeCodes[0] || 'en_US'));
    const defaultOption = this.languageOptions.find((option) => option.localeCode === defaultLocale);

    otcData.preferredLocale = defaultLocale;
    languageControl.setValue(defaultOption?.localeDesc || '', { emitEvent: false });
    this.updateLanguageControlErrors(true, true);
  }

  private updateLanguageControlErrors(hasTypedValue: boolean, hasValidSelection: boolean): void {
    const control = this.languageForm.get('language');
    if (!control) {
      return;
    }

    const currentErrors: ValidationErrors = { ...(control.errors || {}) };
    delete currentErrors['required'];
    delete currentErrors['invalidOption'];

    if (!hasTypedValue) {
      currentErrors['required'] = true;
    } else if (!hasValidSelection) {
      currentErrors['invalidOption'] = true;
    }

    control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
  }

  private ensureValidLanguageSelection(): boolean {
    const languageControl = this.languageForm.get('language');
    if (!languageControl) {
      return true;
    }

    const selectedLanguage = (languageControl.value || '').toString().trim();
    const hasValidPreferredLocale = !!(this.rootScope.activeCustTab?.otcData?.preferredLocale || '').trim();
    this.updateLanguageControlErrors(!!selectedLanguage, hasValidPreferredLocale);

    const hasValidationError = languageControl.hasError('required') || languageControl.hasError('invalidOption');
    if (hasValidationError) {
      languageControl.markAsTouched();
      return false;
    }

    return true;
  }

  isLocaleSelectionInvalid(): boolean {
    const control = this.languageForm.get('language');
    if (!control || !control.touched) {
      return false;
    }
    return control.hasError('required') || control.hasError('invalidOption');
  }

  /**
   * Initializes and synchronizes the OTC form control with the root scope data.
   * This avoids using `[(ngModel)]` on the OTC input because the field is driven by a reactive `FormControl`,
   * and mixing template-driven and reactive form patterns on the same control can cause binding issues.
   * It restores the previously typed OTC value and sets up a subscription to keep the root scope data in sync with the form control.
   */
  private initAndSyncOTC(): void {
    // Restore value
    this.otcService?.oneTimeCode?.patchValue(
      this.rootScope.activeCustTab?.otcData?.typedOTC ?? '',
      { emitEvent: false }
    );

    // Sync changes
    this.otcSubscription = this.otcService?.oneTimeCode?.valueChanges?.subscribe(
      value => {
        this.rootScope.activeCustTab.otcData.typedOTC = value ?? '';
      }
    );
  }

  /**
   * Determines the current active step in the OTC flow.
   * @returns The current active step as a string constant.
   */
  getActiveStep() {
    const { step1Active, step2Active, step3Active } = this.rootScope.activeCustTab?.otcData;

    switch (true) {
      case !step1Active && !step2Active && !step3Active:
        return Constants.AUTHENTICATION_OTC_STEPS.BEFORE_STEP1_ACTIVATE;

      case step1Active && !step2Active && !step3Active:
        return Constants.AUTHENTICATION_OTC_STEPS.ON_STEP1_ACTIVATE;

      case step1Active && step2Active && !step3Active:
        return Constants.AUTHENTICATION_OTC_STEPS.ON_STEP2_ACTIVATE;

      case step1Active && step2Active && step3Active:
        return Constants.AUTHENTICATION_OTC_STEPS.ON_STEP3_ACTIVATE;

      default:
        return 'invalidState';
    }
  }


  /**
   * Handles the continuation of the OTC flow from step 1. 
   * If the selected delivery method is text, it opens the country code dialog.
   * Otherwise, it activates step 2 of the OTC flow.
   */
  continueOnStep1() {
    if (!this.ensureValidLanguageSelection()) {
      return;
    }

    const otcData = this.rootScope.activeCustTab?.otcData;
    if (otcData) {
      otcData.unStopPhoneNumberStatusApiSuccess = false;
      otcData.unStopPhoneNumberStatusApiFailure = false;
      otcData.unStopPhoneNumberStatusApiFailureDetails = '';
    }
    this.rootScope.activeCustTab.otcData.status = '';

    if (this.rootScope.activeCustTab.otcData?.selectedDeliveryMethod?.type == Constants.OTC_MODES.TEXT) {
       const foundDetail = this.rootScope.activeCustTab.otcData.phoneNumberStatusList?.find((item: any) => {
          return item?.phoneNumber === this.rootScope.activeCustTab?.otcData?.selectedDeliveryMethod?.details?.phoneNumber &&
           item?.mblNmbrFrmtCd === this.rootScope.activeCustTab?.otcData?.selectedDeliveryMethod?.details?.countryCode;
        })
      

      this.rootScope.activeCustTab.otcData.selectedCountryCode = this.rootScope.activeCustTab?.otcData?.selectedDeliveryMethod?.details?.countryCode;
      if(foundDetail?.responseCode == 'OPTOUTERR001') { // Invalid country code 
           this.rootScope.activeCustTab.otcData.selectedCountryCode = Constants.COUNTRY_CODES.International; 
        }

      this.otcService.openCountryCodeDialog = true;
    } else {
      this.rootScope.activeCustTab.otcData.step2Active = true;
      this.logger.infoLogs('OTC Step 2 Active for customerSessionId:' + this.rootScope.activeCustTab?.customerSessionId, this.fileName);
    }
  }


  /**
  * Handles the action when the user clicks on the "Back" button to return to step 1 of the OTC flow.
  * It resets various flags and form controls related to the OTC process in the root scope.
  * Additionally, it logs the action for auditing purposes.
  */
  backToStep1() {
    this.rootScope.activeCustTab.otcData.step1Active = true;
    this.rootScope.activeCustTab.otcData.step2Active = false;
    this.rootScope.activeCustTab.otcData.step3Active = false;
    this.rootScope.activeCustTab.otcData.sendOTCSuccess = false;
    this.rootScope.activeCustTab.otcData.otcDenied = false;
    this.rootScope.activeCustTab.otcData.notEligiblePhoneNumber = false;
    this.rootScope.activeCustTab.otcData.generateOTCApiFailure = false;
    this.rootScope.activeCustTab.otcData.typedOTC = '';
    this.rootScope.activeCustTab.otcData.validateOtcFail = false;
    this.rootScope.activeCustTab.otcData.validateOtcLocked = false;
    this.rootScope.activeCustTab.otcData.validateOtcApiFailure = false;
    this.rootScope.activeCustTab.otcData.otcExpired = false;
    this.verifyCodeClicked = false;
    this.rootScope.activeCustTab.otcData.status = '';
    const otcData = this.rootScope.activeCustTab?.otcData;

    if (otcData) {
      otcData.unStopPhoneNumberStatusApiSuccess = false;
      otcData.unStopPhoneNumberStatusApiFailure = false;
      otcData.unStopPhoneNumberStatusApiFailureDetails = '';
    }

    this.otcService.oneTimeCode.reset();
    this.otcService.oneTimeCode.enable();
    this.logger.infoLogs('Send OTC Back button clicked from Step 2 or Cancel button clicked for customerSessionId:' + this.rootScope.activeCustTab?.customerSessionId, this.fileName);
  }


  /**
   * Returns the display number for a given option.
   * @param opt - The option object containing the extension and phone number.
   * @returns The formatted display number.
   */
  getDisplayOption(opt) {
    // CCR1-15052 - Masking phone number and email for display
    if (opt?.phoneNumber || opt?.countryCode) {
      const displayNumber = this.maskPhoneNumberForDisplay(opt?.phoneNumber);
      return `${opt?.countryCode ? opt?.countryCode + ' ' : ''}${displayNumber}`;
    }

    if (opt?.emailAdress) {
      return this.maskEmailForDisplay(opt?.emailAdress);
    }

    return '';
  }

  /**
   * Handles the verification of the One-Time Code (OTC) entered by the user.
   * It retrieves the OTC value from the form control and calls the validateOTC method of the OtcService.
   * Additionally, it logs the action for auditing purposes.
   */
  verifyCode() {
    const otc = this.otcService?.oneTimeCode?.value;
    this.verifyCodeClicked = true;
    this.otcService.validateOTC(otc);
    this.logger.infoLogs('OTC Verify Code Clicked for customerSessionId:' + this.rootScope.activeCustTab?.customerSessionId, this.fileName);
  }

  /**
   * Handles the change in the selected delivery method.
   * @param parentType - The type of the selected delivery method.
   * @param detailValue - The details of the selected delivery method.
   */
  onSelectionChange(parentType: string, detailValue: any) {
    const otcData = this.rootScope.activeCustTab?.otcData;
    if (otcData) {
      otcData.unStopPhoneNumberStatusApiSuccess = false;
      otcData.unStopPhoneNumberStatusApiFailure = false;
      otcData.unStopPhoneNumberStatusApiFailureDetails = '';
    }
    this.rootScope.activeCustTab.otcData.selectedDeliveryMethod = { type: parentType, details: detailValue };

    if (parentType === Constants.OTC_MODES.TEXT) {
      this.rootScope.activeCustTab.otcData.selectedPhoneNumber = detailValue?.phoneNumber;
      this.rootScope.activeCustTab.otcData.selectedCountryCode = detailValue?.countryCode;

      const foundDetail = this.rootScope.activeCustTab.otcData.phoneNumberStatusList?.find((item: any) => {
          return item?.phoneNumber === detailValue?.phoneNumber && item?.mblNmbrFrmtCd === detailValue?.countryCode;
        })
      

      if(foundDetail?.responseCode == 'OPTOUTERR001') { // Invalid country code 
          //As per CCR1-13754, if the country code is invalid, we will set it to International and show the error message.
           this.rootScope.activeCustTab.otcData.selectedCountryCode = Constants.COUNTRY_CODES.International; 
        }
       
      
    } else if (parentType === Constants.OTC_MODES.EMAIL) {
      this.rootScope.activeCustTab.otcData.selectedEmail = detailValue?.emailAdress;
    }
  }


  /**
 * Checks if the selected delivery method is empty, or if it's the only configured
 * delivery method and it's a stopped text number, in which case Continue must stay disabled.
 * @returns A boolean indicating whether the Continue button should be disabled.
 */
  isSelectedDeliveryMethodEmpty(): boolean {
    if (!this.rootScope.activeCustTab?.otcData?.selectedDeliveryMethod?.details ||
      Object.keys(this.rootScope.activeCustTab?.otcData?.selectedDeliveryMethod?.details)?.length === 0) {
      return true;
    }

    const methodsWithData = this.rootScope.activeCustTab?.otcData?.otcConfigData?.deliveryMethods
      ?.filter((m: any) => m?.details && m?.details?.length > 0);

    if (methodsWithData?.length === 1 && methodsWithData?.[0]?.type === Constants.OTC_MODES.TEXT &&
      methodsWithData?.[0]?.details?.length === 1 && this.isTextStopped(methodsWithData?.[0]?.details?.[0])) {
      return true;
    }

    return false;
  }

  /**
   * CCR3-13754 Displays a confirmation dialog when the participant has previously unsubscribed from text messages.
   */
  stopTextMsgDialog() {
    this.rootScope.openDialog({
      title: '',
      subheading: '',
      message: Constants.Stop_Text_Msg,
      firstbutton: 'Cancel',
      secondbutton: 'Got it',
      crossIcon: false,
      comingFrom: 'option-otc',
      additionalClass: 'stop-msg',
    });
  }

  ngOnDestroy() {
    this.rootScope.activeCustTab.otcData.validateOTCApiProcessing = false;
    this.otcSubscription?.unsubscribe();

  }

  /**
   *  {
                        "phoneNumber": "8889786990",
                        "countryCode": "US",
                        "isPreferred": "false"
                    },
   * @param detail 
   * @returns 
   */
  isTextStopped(detail: any): boolean {
    if(this.rootScope.activeCustTab.otcData?.phoneNumberStatusList?.length > 0) {
        const foundDetail = this.rootScope.activeCustTab.otcData.phoneNumberStatusList.find((item: any) => {
          return item?.phoneNumber === detail?.phoneNumber && item?.mblNmbrFrmtCd === detail?.countryCode;
        })

      
      if(foundDetail?.responseCode == 'OPTOUT000102') { // Case 2: Opt Out case .msg to be displayed
          return true;
        }
        else {
          return false;
        }
      
    }
    else{
      return false;
    }
  }

  isTextBlocked(detail: any): boolean {
    if(this.rootScope.activeCustTab.otcData?.phoneNumberStatusList?.length > 0) {
        const foundDetail = this.rootScope.activeCustTab.otcData.phoneNumberStatusList.find((item: any) => {
          return item?.phoneNumber === detail?.phoneNumber && item?.mblNmbrFrmtCd === detail?.countryCode;
        })

      if(foundDetail?.responseCode == 'OPTOUT000100') { // Case 3: Text Blocked case .msg to be displayed
          return true;
        }
        else {
          return false;
        }
    }
    else{
      return false;
    }
  }


  /**
   * Displays a confirmation dialog asking the agent to confirm opting the participant back in to text messages.
   * Story: CCR1-13755 @author - Sultan Khan
   */
  confirmUnblockMessage(detail: any) {
    this.rootScope.openDialog({
      title: '',
      subheading: '',
      message: 'The participant previously unsubscribed from text messages by replying "STOP." Please verify that they would like to opt back in before continuing.',
      secondMessage: 'Please note: A participant can only reverse a STOP request once every 30 days.',
      firstbutton: 'Cancel',
      secondbutton: 'Confirm',
      crossIcon: false,
      comingFrom: 'otc',
      additionalClass: 'unblock-msg',
      rowData: detail
    });
  }


  /**
   * Retrieves the stop text opt-in configuration status for the active client.
   * @returns {boolean} True if stop text opt-in is enabled for the client, false otherwise.
   * Story: CCR1-13755 @author - Sultan Khan
   */
  getStopTextOptInConfig(): boolean {
    const clientConfigValue = this.rootScope.stopTextOptInConfig?.get(this.rootScope.activeCustTab?.clientId);
    return clientConfigValue?.is_enabled === 'true';
  }

  maskPhoneNumberForDisplay(phoneNumber?: string | null): string {
    const raw = String(phoneNumber ?? '').trim();
    if (!raw) return '';

    const digits = raw?.replace(/\D/g, '');
    if (digits?.length <= 4) return raw;

    const maskedChars = '*'.repeat(digits?.length - 4);
    return `${maskedChars}${digits?.slice(-4)}`;
  }

  maskEmailForDisplay(email?: string | null): string {
    const value = String(email ?? '').trim();
    if (!value || !value.includes('@')) return value;

    const [localPart, domain] = value.split('@');
    if (!localPart || !domain) return value;

    const first = localPart?.charAt(0);
    const last = localPart?.charAt(localPart?.length - 1);
    const middle = '*'.repeat(Math.max(localPart?.length - 2, 0));

    return `${first}${middle}${last}@${domain}`;
  }

  }
